import {
  Box,
  IconButton,
  IconButtonProps,
  Modal as MuiModal,
  Slide,
  styled,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
  useMediaQuery,
} from "@mui/material";
import React, { ReactElement, ReactNode } from "react";
import CloseIcon from "@icons/cross.svg?react";
import { LoadingPlaceHolder } from "../common/progress/LoadingPlaceHolder";

const ALLOWED_CLICK_TARGETS = [
  ".range-date-picker-paper",
  ".range-date-picker-backdrop",
  ".mui-menu-backdrop",
  ".mui-menu-paper",
  ".MuiDialog-container",
  "#modal-wrapper",
];

type ModalPosition = "centered" | "right" | "bottom";

const getHeight = (
  width: number | string,
  height: number | string,
  maxHeight: number | string | undefined,
  fitContent: boolean,
  modalPosition: ModalPosition
) => {
  if (fitContent) {
    return {
      height: "fit-content",
      width: "fit-content",
    };
  }

  return {
    width: "100%",
    maxWidth: typeof width === "string" ? width : `${width}px`,
    maxHeight:
      typeof maxHeight === "number"
        ? `${maxHeight}px`
        : (maxHeight ?? typeof height === "number")
          ? `${height}px`
          : height,
    height:
      modalPosition === "bottom" && height === "auto"
        ? "calc(100% - 24px)"
        : height === "auto"
          ? "auto"
          : height,
  };
};

const getMaxHeight = (
  modalPosition: ModalPosition,
  navigationIncluded: boolean = true,
  maxHeight?: number | string
) => {
  const TOP_NAVIGATION_HEIGHT = navigationIncluded ? 54 : 0;
  const BOTTOM_NAVIGATION_HEIGHT = 54;
  const PADDING = 20;

  const windowMaxHeight =
    window.innerHeight -
    TOP_NAVIGATION_HEIGHT -
    (modalPosition === "bottom" ? 0 : BOTTOM_NAVIGATION_HEIGHT) -
    PADDING;

  if (maxHeight === "100%") return windowMaxHeight;

  if (maxHeight) {
    return (typeof maxHeight === "string" ? parseFloat(maxHeight) : maxHeight) >
      windowMaxHeight
      ? windowMaxHeight
      : maxHeight;
  } else {
    return windowMaxHeight;
  }
};

const StyledMuiModal = styled(MuiModal, {
  shouldForwardProp: (prop) =>
    prop !== "backdropStyle" && prop !== "interactiveBackground",
})<{ backdropStyle?: "dark" | "blur"; interactiveBackground?: boolean }>(
  ({ theme, backdropStyle, interactiveBackground }) => ({
    "& .MuiBackdrop-root": {
      backgroundColor:
        backdropStyle === "dark" ? "rgba(0, 0, 0, 0.5)" : "transparent",
      backdropFilter: "blur(6px)",
      pointerEvents: interactiveBackground ? "all" : "none",
    },
    pointerEvents: interactiveBackground ? "none" : "all",
  })
);

export const CloseButton = ({
  onClose,
  isSmall = false,
  ...rest
}: {
  onClose: () => void;
  isSmall?: boolean;
} & IconButtonProps) => {
  const iconSize = isSmall ? "16px" : "26px";

  return (
    <IconButton onClick={onClose} {...rest}>
      <CloseIcon width={iconSize} height={iconSize} />
    </IconButton>
  );
};

const OuterModalWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "modalPosition",
})<{ modalPosition: ModalPosition }>(({ modalPosition }) => ({
  display: "flex",
  alignItems: modalPosition === "bottom" ? "flex-end" : "center",
  justifyContent: modalPosition === "centered" ? "center" : "flex-end",
  height: "100%",
  width: "100%",
  position: "absolute",
  outline: "none !important",
  top: 0,
  right: modalPosition === "right" ? "12px" : 0,
}));

const InnerModalWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    ![
      "modalPosition",
      "contentPadding",
      "fitContent",
      "borderRadius",
      "interactiveBackground",
      "bgColor",
      "boxShadow",
      "isRelative",
    ].includes(prop as string),
})<{
  width: number | string;
  height: number | string;
  maxHeight: number | string | undefined;
  modalPosition: ModalPosition;
  contentPadding?: string;
  fitContent: boolean;
  borderRadius?: string;
  interactiveBackground?: boolean;
  bgColor?: string;
  boxShadow?: string;
  isRelative?: boolean;
}>(
  ({
    theme,
    width,
    height,
    contentPadding,
    fitContent,
    modalPosition,
    maxHeight,
    borderRadius,
    interactiveBackground,
    bgColor,
    boxShadow,
    isRelative,
  }) => ({
    ...getHeight(width, height, maxHeight, fitContent, modalPosition),
    ...(isRelative && { position: "relative" as const }),
    backgroundColor: bgColor ?? theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    padding: contentPadding ? contentPadding : theme.spacing(4, 5),
    outline: "none !important",
    overflowY: "auto",
    borderRadius: borderRadius
      ? borderRadius
      : modalPosition === "bottom"
        ? "8px 8px 0 0"
        : "8px",
    border: `1px solid ${theme.palette.border.secondary}`,
    boxShadow:
      boxShadow ?? (theme.type === "finanzfluss" ? theme.shadows[1] : "none"),
    pointerEvents: interactiveBackground ? "all" : "auto",
    [theme.breakpoints.down("sm")]: {
      right: "unset",
      padding: contentPadding ? contentPadding : theme.spacing(6),
      borderRadius: height === "100%" && "0",
    },
  })
);

export type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  onCloseButtonClick?: () => void;
  title?: string | ReactElement;
  subtitle?: string;
  children: ReactNode;
  width?: number | string;
  height?: number | string;
  maxHeight?: number | string;
  testId?: string;
  contentPadding?: string;
  titleVariant?: TypographyProps["variant"];
  subtitleVariant?: TypographyProps["variant"];
  position?: ModalPosition;
  animation?: "none" | "slide-up" | "slide-left";
  isFetching?: boolean;
  actionButton?: ReactNode;
  disablePortal?: boolean;
  disableScrollLock?: boolean;
  zIndex?: number;
  backdropStyle?: "dark" | "blur";
  borderRadius?: string;
  titleMargin?: number;
  fitContent?: boolean;
  interactiveBackground?: boolean;
  backgroundColor?: string;
  outerSx?: SxProps;
  boxShadow?: string;
  isRelative?: boolean;
};

export const Modal = ({
  isOpen,
  onClose,
  onCloseButtonClick,
  title,
  subtitle,
  children,
  testId,
  width = 700,
  height = "auto",
  maxHeight = "100%",
  contentPadding,
  titleVariant = "h6",
  subtitleVariant = "body2",
  position = "centered",
  backdropStyle = "dark",
  actionButton,
  borderRadius,
  titleMargin,
  isFetching = false,
  disablePortal = true,
  disableScrollLock = false,
  fitContent = false,
  zIndex,
  interactiveBackground = false,
  backgroundColor,
  outerSx,
  boxShadow,
  isRelative,
}: ModalProps) => {
  const isLessSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const closeModal = onClose ?? onCloseButtonClick;
  const calculatedMaxHeight = isLessSm
    ? getMaxHeight(position, zIndex === undefined || zIndex <= 4000, maxHeight)
    : maxHeight;
  const [isFocusing, setIsFocusing] = React.useState(false);

  const handleClose = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      !ALLOWED_CLICK_TARGETS.some((target) =>
        (e.target as HTMLElement).closest(target)
      ) &&
      !isFocusing &&
      !interactiveBackground
    ) {
      onClose?.();
    }
  };

  const handleOuterWrapperMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      !(e.target as HTMLElement).closest("#modal-wrapper") &&
      !interactiveBackground
    ) {
      setIsFocusing(false);
    }
  };

  const innerContent = (
    <OuterModalWrapper
      modalPosition={position}
      onMouseDown={handleOuterWrapperMouseDown}
      onMouseUp={handleClose}
      sx={outerSx}
    >
      <InnerModalWrapper
        id="modal-wrapper"
        width={width}
        height={height}
        maxHeight={calculatedMaxHeight}
        fitContent={fitContent}
        data-testid={testId}
        modalPosition={position}
        contentPadding={contentPadding}
        borderRadius={borderRadius}
        interactiveBackground={interactiveBackground}
        onMouseDown={() => setIsFocusing(true)}
        onMouseUp={() => setIsFocusing(false)}
        bgColor={backgroundColor}
        boxShadow={boxShadow}
        isRelative={isRelative}
      >
        {typeof title === "string" ? (
          <Box className="modal-heading-container">
            <Box
              mb={subtitle || titleMargin !== undefined ? titleMargin : 6}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={fitContent ? 4 : "unset"}
            >
              <Typography variant={titleVariant}>{title}</Typography>
              <Box display="flex" alignItems="center" gap={4}>
                {actionButton}
                {closeModal && (
                  <CloseButton
                    onClose={closeModal}
                    data-testid="modal-close-button"
                  />
                )}
              </Box>
            </Box>
            {subtitle && (
              <Typography
                variant={subtitleVariant}
                color="textSecondary"
                mb={6}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        ) : (
          title
        )}
        {isFetching ? <LoadingPlaceHolder height="100%" /> : children}
      </InnerModalWrapper>
    </OuterModalWrapper>
  );

  return (
    <StyledMuiModal
      disablePortal={disablePortal}
      disableScrollLock={disableScrollLock}
      open={isOpen}
      onClose={() => closeModal?.()}
      backdropStyle={backdropStyle}
      hideBackdrop={interactiveBackground}
      disableEnforceFocus={interactiveBackground}
      interactiveBackground={interactiveBackground}
      sx={{
        zIndex: zIndex ? zIndex : undefined,
      }}
    >
      {position === "right" || position === "bottom" || height === "100%" ? (
        <Slide in={isOpen} direction={position === "right" ? "left" : "up"}>
          {innerContent}
        </Slide>
      ) : (
        innerContent
      )}
    </StyledMuiModal>
  );
};
